import {
  Box,
  FieldWrapper,
  FormField,
  FormGrid,
  Page,
  Text,
} from '@dixa/lib-shared-habla';
import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { numbersApi } from 'apis/numbers';
import { HookFormPhoneNumberSelect } from 'components/hookForm';
import { actions as personalActions } from 'components/settings/actions/personal';
// eslint-disable-next-line no-restricted-imports
import { ToggleBox, ToggleBoxHead } from 'fable/toggleBox';
import { articles } from 'utils/elevio';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { ProfileFormFieldValues } from '../../profileForm.types';

import styles from './profileFormSettings.module.scss';

export const ProfileFormSettings = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { control, setValue } = useFormContext<ProfileFormFieldValues>();
  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  const personalState = useAppSelector((state) => state.settings.personal);
  const userDefaultOutboundNumber = useAppSelector(
    (state) => state.settings.profile.profile.default_outbound_number,
  );
  const organizationDefaultOutboundNumber = useAppSelector(
    (state) => state.settings.organization.organization.default_outbound_number,
  );
  const { defaultOutboundNumber } = numbersApi.useReadDefaultNumberQuery(
    undefined,
    {
      skip: !userDefaultOutboundNumber,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        defaultOutboundNumber: data?.data,
      }),
    },
  );

  // Sets the initial value of the default outbound number field to the user's preferred default. We
  // do this check because in case the user doesn't have a preferred default, the numbers API will
  // return the organization default if it exists, and we do not want to use that as the initial
  // value in the form, to prevent the organization default from being set as the user default.
  useEffect(() => {
    if (
      defaultOutboundNumber &&
      defaultOutboundNumber.e164_format === userDefaultOutboundNumber
    ) {
      setValue('default_outbound_number', [defaultOutboundNumber]);
    }
  }, [defaultOutboundNumber, setValue, userDefaultOutboundNumber]);

  const handleChangeKeyboardShortcuts = useCallback(
    (value: boolean) => {
      dispatch(
        personalActions.personalKeyboardShortcuts({
          key: 'keyboardShortcuts',
          value,
        }),
      );
    },
    [dispatch],
  );

  return (
    <Page.Section title="Settings">
      <FormGrid>
        <FormField span={isSmallScreen ? 6 : 3}>
          <HookFormPhoneNumberSelect
            name="default_outbound_number"
            label="Default outbound number"
            control={control}
            rules={{
              validate: {
                isOutbound: (phoneNumber) =>
                  phoneNumber?.length
                    ? phoneNumber[0].outgoing_functionality ||
                      'The selected phone number does not support outbound calls'
                    : true,
              },
            }}
            componentProps={{
              showCountryFlag: true,
              clearable: true,
            }}
            helpIcon={
              !userDefaultOutboundNumber && organizationDefaultOutboundNumber
                ? {
                    text: `If you don't select your preferred default outbound number, the organization's default outbound number will be used: ${organizationDefaultOutboundNumber}`,
                    dark: true,
                    tooltipClassName: styles.tooltip,
                  }
                : undefined
            }
          />
        </FormField>

        <FormField span={isSmallScreen ? 6 : 3}>
          <FieldWrapper label="Keyboard shortcuts">
            <ToggleBox
              title={`Keyboard shortcuts ${
                personalState.keyboardShortcuts ? 'enabled' : 'disabled'
              }`}
              isOpen={personalState.keyboardShortcuts}
              headComponent={ToggleBoxHead}
              textProps={{ variant: '3' }}
              input={{
                name: 'keyboard_shortcuts',
                value: 'keyboard_shortcuts',
              }}
              onChange={handleChangeKeyboardShortcuts}
              disabled={personalState.meta.asyncStatus === 'loading'}
            >
              <Box padding="0 300 300">
                <Text.ExternalLink
                  href="#"
                  data-elevio-article={
                    articles.settings.profile.keyboardShortcuts
                  }
                  data-elevio-style="nothing"
                >
                  See all the keyboard shortcuts
                </Text.ExternalLink>
              </Box>
            </ToggleBox>
          </FieldWrapper>
        </FormField>
      </FormGrid>
    </Page.Section>
  );
};
